import React,{useState} from 'react';
import Dialog from './Dialog';
import {FiChevronDown,FiDownload,FiFileText,FiHeadphones,FiSettings} from 'react-icons/fi';
import {formatTime} from '../lib/editModel';
import {sequenceDuration} from '../lib/sequence';


const frameRates=[[24000,1001,'23.976'],[24,1,'24'],[25,1,'25'],[30000,1001,'29.97'],[30,1,'30'],[50,1,'50'],[60000,1001,'59.94'],[60,1,'60']];
const verifiedStatuses=['confirmed-cfr','detected-cfr','demo-cfr'];
const metadataReady=data=>Boolean(data?.filename&&verifiedStatuses.includes(data?.frameStatus)&&
 Number(data.duration)>0&&Number(data.width)>=16&&Number(data.height)>=16&&
 Number(data.fpsNum)>0&&Number(data.fpsDen)>0&&Number(data.audioSampleRate)>0&&
 Number.isInteger(Number(data.audioChannels))&&Number(data.audioChannels)>=0&&Number(data.audioChannels)<=16);

/** Choosing an output never depends on the editor tab currently open. */
export default function ExportDialog({project,paper,interviews=[],defaultInterviewId,title,onExport,onTranslate,onSaveSourceMetadata,onClose,error,audioExportStatus=null,onCancelAudio=()=>{}}) {
 const blocks=paper?.blocks||[];
 const hasAssembly=blocks.length>0;
 const [source,setSource]=useState(hasAssembly?'assembly':'interview');
 const [advancedOpen,setAdvancedOpen]=useState(false),[advancedId,setAdvancedId]=useState('');
 const [format,setFormat]=useState('xml');
 const [moreOpen,setMoreOpen]=useState(false),[subtitleMode,setSubtitleMode]=useState('original');
 const exportingAudio=Boolean(audioExportStatus);
 const [metadataOverrides,setMetadataOverrides]=useState({});
 const [metadataWarning,setMetadataWarning]=useState(''),[metadataSaved,setMetadataSaved]=useState(false),[metadataSaving,setMetadataSaving]=useState(false);
 const [interviewId,setInterviewId]=useState(
   interviews.some(row=>row.id===defaultInterviewId)?defaultInterviewId:interviews[0]?.id||''
 );
 const row=interviews.find(item=>item.id===interviewId)||interviews[0];
 const interview={...(row?.data||project),...(metadataOverrides[row?.id]||{})};
 const ids=source==='assembly'?[...new Set(blocks.map(block=>block.sourceId))]:[row?.id||'local'];
 const candidates=ids.map(id=>{
  const match=interviews.find(item=>item.id===id),snapshot=paper?.sources?.[id]||{};
  const data={...snapshot,...(match?.data||((id==='local'||id===row?.id)?interview:{})),...(metadataOverrides[id]||{})};
  return {id,data,name:match?.data?.displayName||data?.title||data?.filename||'Unnamed interview'};
 });
 const sources=source==='assembly'?candidates.map(c=>c.data):[interview];
 const incomplete=candidates.find(c=>!metadataReady(c.data));
 const chosen=candidates.find(c=>c.id===advancedId)||incomplete||candidates[0];
 const duration=source==='assembly'
   ? blocks.reduce((total,block)=>total+Math.max(0,block.end-block.start),0)
   : sequenceDuration(interview);
 const submit=kind=>{
  if(['xml','resolve-xml','resolve-edl','fcpxml'].includes(kind)){
   const variable=candidates.find(c=>c.data.frameStatus==='variable-fps');
   if(variable){
    setMetadataWarning(variable.name+' has variable frame timing. Convert and reimport the source before making a timeline.');
    setAdvancedOpen(true);setAdvancedId(variable.id);return;
   }
   if(incomplete){
    setMetadataWarning('Confirm original video details for '+incomplete.name+' before exporting the timeline.');
    setAdvancedOpen(true);setAdvancedId(incomplete.id);return;
   }
  }
  setMetadataWarning('');
  onExport(kind,source,source==='interview'?row?.id:null,metadataOverrides);
 };
 const isTimeline=['xml','resolve-xml','fcpxml','resolve-edl'].includes(format);
 const formatNames={xml:'Premiere Pro XML','resolve-xml':'Resolve XML',fcpxml:'Final Cut XML','resolve-edl':'Resolve EDL',txt:'Transcript',srt:'Subtitles',mp3:'Audio rough cut (.mp3)'};
 const downloadSelected=()=>format==='srt'&&subtitleMode==='translated'
  ?onTranslate(source,source==='interview'?row?.id:null):submit(format);
 const saveMetadata=async event=>{
  event.preventDefault();setMetadataWarning('');setMetadataSaved(false);
  if(!chosen)return;
  if(chosen.data.frameStatus==='variable-fps'){
   setMetadataWarning('Variable frame rate cannot be fixed by changing metadata. Convert and reimport the video.');return;
  }
  const data=new FormData(event.currentTarget);
  const [fpsNum,fpsDen]=String(data.get('rate')||'').split('/').map(Number);
  const filename=String(data.get('filename')||'').trim();
  const width=Number(data.get('width')),height=Number(data.get('height'));
  const audioChannels=Number(data.get('channels')),audioSampleRate=Number(data.get('sampleRate'));
  if(!fpsNum||!fpsDen||!filename||filename.length>255||filename.includes('/')||filename.includes('\\')||[...filename].some(character=>character.charCodeAt(0)<32)||
     !Number.isSafeInteger(width)||!Number.isSafeInteger(height)||width<16||height<16||
     !Number.isSafeInteger(audioChannels)||audioChannels<0||audioChannels>16||
     !Number.isSafeInteger(audioSampleRate)||audioSampleRate<1){
   setMetadataWarning('Check the original filename, frame rate, resolution and audio information.');return;
  }
  setMetadataSaving(true);
  try{
   const next=await onSaveSourceMetadata(chosen.id,{
    filename,width,height,fpsNum,fpsDen,audioChannels,audioSampleRate,
    timecode:String(data.get('timecode')||'00:00:00:00').trim(),frameStatus:'confirmed-cfr'
   });
   setMetadataOverrides(old=>({...old,[chosen.id]:{...chosen.data,...next}}));
   setMetadataSaved(true);
  }catch(e){setMetadataWarning(e.message||'Could not save source information.');}
  finally{setMetadataSaving(false);}
 };


 return <Dialog title="Export" onClose={onClose} className="export-dialog export-simple">
  <div className="export-simple-project">
   <strong>{source==='assembly'?title||'Your project':interview.displayName||interview.title||interview.filename||'Interview'}</strong>
   <span>{formatTime(duration)} · {source==='assembly'?
     sources.length+' interviews · '+blocks.length+' passages':'Single interview edit'}</span>
  </div>
  <fieldset className="export-source-switch export-simple-source">
   <legend>Export from</legend>
   <div className="export-source-options">
    <label className={'export-source-option'+(source==='assembly'?' is-selected':'')+(hasAssembly?'':' is-disabled')}>
     <input type="radio" name="export-target" value="assembly" checked={source==='assembly'}
      disabled={!hasAssembly} onChange={()=>{setSource('assembly');setMetadataWarning('');}}/>
     <span>Assembly</span>
    </label>
    <label className={'export-source-option'+(source==='interview'?' is-selected':'')}>
     <input type="radio" name="export-target" value="interview" checked={source==='interview'}
      onChange={()=>{setSource('interview');setMetadataWarning('');}}/>
     <span>Single interview</span>
    </label>
   </div>
  </fieldset>
  {source==='interview'&&<label className="export-interview-picker" htmlFor="export-interview-id">
   <span>Choose interview</span>
   <select id="export-interview-id" value={row?.id||''} onChange={event=>setInterviewId(event.target.value)}>
    {interviews.map(item=><option key={item.id} value={item.id}>
     {item.data?.displayName||item.data?.title||item.data?.filename||'Unnamed interview'}
    </option>)}
   </select>
  </label>}
  <fieldset className="export-simple-formats">
   <legend>Format</legend>
   <div className="export-simple-grid">
    <label className={'export-simple-format'+(format==='xml'?' is-selected':'')}>
     <input type="radio" name="export-format" value="xml" checked={format==='xml'} onChange={()=>setFormat('xml')}/>
     <img className="export-brand-logo" src="/brand/export-premiere-pro.svg" width="28" height="28" alt="" aria-hidden="true"/>
     <span>Premiere Pro XML</span>
    </label>
    <label className={'export-simple-format'+(format==='resolve-xml'?' is-selected':'')}>
     <input type="radio" name="export-format" value="resolve-xml" checked={format==='resolve-xml'} onChange={()=>setFormat('resolve-xml')}/>
     <img className="export-brand-logo" src="/brand/export-davinci-resolve.svg" width="28" height="28" alt="" aria-hidden="true"/>
     <span>DaVinci Resolve XML</span>
    </label>
    <label className={'export-simple-format'+(format==='fcpxml'?' is-selected':'')}>
     <input type="radio" name="export-format" value="fcpxml" checked={format==='fcpxml'} onChange={()=>setFormat('fcpxml')}/>
     <img className="export-brand-logo" src="/brand/export-final-cut-pro.svg" width="28" height="28" alt="" aria-hidden="true"/>
     <span>Final Cut Pro XML</span>
    </label>
    <label className={'export-simple-format'+(format==='srt'?' is-selected':'')}>
     <input type="radio" name="export-format" value="srt" checked={format==='srt'} onChange={()=>setFormat('srt')}/>
     <FiFileText className="export-simple-format-icon" aria-hidden="true"/>
     <span>Subtitles (.srt)</span>
    </label>
   </div>
  </fieldset>
  <details className="export-simple-more" open={moreOpen} onToggle={event=>setMoreOpen(event.currentTarget.open)}>
   <summary>More formats <span>MP3 · EDL · Transcript</span><FiChevronDown aria-hidden="true"/></summary>
   <div className="export-simple-extra">
    <label className={'export-simple-format'+(format==='resolve-edl'?' is-selected':'')}>
     <input type="radio" name="export-format" value="resolve-edl" checked={format==='resolve-edl'} onChange={()=>setFormat('resolve-edl')}/>
     <img className="export-brand-logo" src="/brand/export-davinci-resolve.svg" width="28" height="28" alt="" aria-hidden="true"/>
     <span>Resolve EDL</span>
    </label>
    <label className={'export-simple-format'+(format==='txt'?' is-selected':'')}>
     <input type="radio" name="export-format" value="txt" checked={format==='txt'} onChange={()=>setFormat('txt')}/>
     <FiFileText className="export-simple-format-icon" aria-hidden="true"/>
     <span>Transcript (.txt)</span>
    </label>
    <label className={'export-simple-format'+(format==='mp3'?' is-selected':'')}>
     <input type="radio" name="export-format" value="mp3" checked={format==='mp3'} onChange={()=>setFormat('mp3')}/>
     <FiHeadphones className="export-simple-format-icon" aria-hidden="true"/>
     <span>Audio rough cut (.mp3)</span>
    </label>
   </div>
  </details>
  {format==='srt'&&<fieldset className="export-simple-subtitles">
   <legend>Subtitle language</legend>
   <div className="export-simple-subtitle-options">
    <label className={subtitleMode==='original'?'is-selected':''}>
     <input type="radio" name="subtitle-mode" value="original" checked={subtitleMode==='original'} onChange={()=>setSubtitleMode('original')}/>
     Original
    </label>
    <label className={subtitleMode==='translated'?'is-selected':''}>
     <input type="radio" name="subtitle-mode" value="translated" checked={subtitleMode==='translated'} onChange={()=>setSubtitleMode('translated')}/>
     AI translation
    </label>
   </div>
  </fieldset>}
  {format==='mp3'&&<p className="export-audio-hint">One MP3 with your selected dialogue in order. No video or music is added.</p>}
  {(metadataWarning||error)&&<p className="export-source-warning" role="alert">{metadataWarning||error}</p>}
  <div className="export-simple-footer">
   <div className="export-simple-footer-actions">
    <button type="button" className="export-simple-cancel" onClick={exportingAudio?onCancelAudio:onClose}>{exportingAudio?'Cancel export':'Cancel'}</button>
    <button type="button" className="export-simple-download" disabled={exportingAudio} onClick={downloadSelected}>
     {exportingAudio?'Creating MP3…':format==='srt'&&subtitleMode==='translated'?<><FiFileText aria-hidden="true"/> Translate subtitles</>:<><FiDownload aria-hidden="true"/> Download {formatNames[format]}</>}
    </button>
   </div>
   {exportingAudio&&<div className="export-audio-progress" role="status" aria-live="polite">
    <span>Preparing MP3 · {audioExportStatus.percent}% {audioExportStatus.count>0? '· passage '+audioExportStatus.passage+'/'+audioExportStatus.count:''}</span>
    <progress max="100" value={audioExportStatus.percent}/>
   </div>}
   {isTimeline&&<div className="export-simple-caption">Timeline only · Relink your original footage in the editor.</div>}
   <details className="export-advanced export-simple-advanced" open={advancedOpen}
    onToggle={event=>setAdvancedOpen(event.currentTarget.open)}>
    <summary><FiSettings className="export-advanced-icon" aria-hidden="true"/>
     <span>Advanced source settings</span>
     {isTimeline&&incomplete&&<span className="export-source-readiness needs-attention">Review required</span>}
     <FiChevronDown className="export-advanced-chevron" aria-hidden="true"/>
    </summary>
    <div className="export-advanced-body">
     {source==='assembly'&&<div className="export-simple-source-list">
      <strong>Included interviews</strong>
      <ul>{candidates.map(item=><li key={item.id}>{item.name}</li>)}</ul>
     </div>}
     {candidates.length>1&&<label className="export-interview-picker">Source interview
      <select value={chosen?.id||''} onChange={event=>{setAdvancedId(event.target.value);setMetadataWarning('');setMetadataSaved(false);}}>
       {candidates.map(candidate=><option key={candidate.id} value={candidate.id}>
        {candidate.name}{metadataReady(candidate.data)?' · ready':' · review required'}
       </option>)}
      </select>
     </label>}
         {chosen&&<form key={chosen.id} className="export-source-editor" onSubmit={saveMetadata}>
     <p className="export-source-editor-help">Use the original camera file's properties, not those of a proxy. Frame rate is important for accurate Premiere, Resolve and Final Cut Pro edits.</p>
     {chosen.data.frameStatus==='variable-fps'&&<p className="export-source-warning" role="alert">Variable frame rate detected. Convert and reimport this video before frame-accurate XML or EDL export.</p>}
     <div className="export-source-grid">
      <label className="is-wide">Original frame rate
       <select name="rate" required defaultValue={chosen.data.fpsNum&&chosen.data.fpsDen?String(chosen.data.fpsNum)+'/'+String(chosen.data.fpsDen):''} disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}>
        <option value="" disabled>Choose original frame rate</option>
        {frameRates.map(([n,d,label])=><option key={label} value={String(n)+'/'+String(d)}>{label} fps</option>)}
        {chosen.data.fpsNum&&chosen.data.fpsDen&&!frameRates.some(([n,d])=>n===chosen.data.fpsNum&&d===chosen.data.fpsDen)&&
         <option value={String(chosen.data.fpsNum)+'/'+String(chosen.data.fpsDen)}>{(chosen.data.fpsNum/chosen.data.fpsDen).toFixed(3)} fps (saved)</option>}
       </select>
      </label>
      <label className="is-wide">Original filename
       <input name="filename" defaultValue={chosen.data.filename||''} maxLength={255} required disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
      <label>Original width
       <input name="width" type="number" min="16" max="16384" defaultValue={chosen.data.width||''} required disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
      <label>Original height
       <input name="height" type="number" min="16" max="16384" defaultValue={chosen.data.height||''} required disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
      <label>Audio channels
       <input name="channels" type="number" min="0" max="16" defaultValue={chosen.data.audioChannels??2} required disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
      <label>Sample rate (Hz)
       <input name="sampleRate" type="number" min="1" max="384000" defaultValue={chosen.data.audioSampleRate||48000} required disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
      <label className="is-wide">Original source timecode
       <input name="timecode" defaultValue={chosen.data.timecode||'00:00:00:00'} disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}/>
      </label>
     </div>
     {chosen.data.frameStatus!=='variable-fps'&&<label className="export-source-verify">
      <input type="checkbox" name="confirmed" required disabled={metadataSaving}/>
      I verified that the original video uses a constant frame rate.
     </label>}
     <p className="export-source-editor-help">A proxy must start on the same frame, maintain the original frame rate and run for the same duration. Takefold does not convert footage or conform timecode.</p>
     <button className="export-source-save" type="submit" disabled={metadataSaving||chosen.data.frameStatus==='variable-fps'}>{metadataSaving?'Saving…':'Save source information'}</button>
     {metadataSaved&&<p className="export-source-saved" role="status">Source information saved. Ready to export.</p>}
    </form>}
    </div>
   </details>
  </div>
 </Dialog>;
}
